/*
 * SLEEKIOUS — theme behaviour (vanilla JS + GSAP/ScrollTrigger, no build step).
 *
 *  1. utilities, world colour system, in-view flags
 *  2. intro loader
 *  3. header, dialogs (menu · search · cart), predictive search
 *  4. cart (Ajax API + Section Rendering), quick add, product form
 *  5. cursor + magnetic buttons
 *  6. section behaviours (hero, worlds, switch, marquee, new drop, film, story, custom, reveals…)
 *  7. Shopify theme editor hooks
 */
(() => {
  "use strict";

  /* ------------------------------------------------------------------ 1 */

  const cfg = window.Sleekious || { routes: {}, strings: {} };
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  const hasGsap = Boolean(gsap && ScrollTrigger);
  if (hasGsap) {
    gsap.registerPlugin(ScrollTrigger);
    // Display fonts change line heights once loaded — re-measure every trigger.
    document.fonts?.ready.then(() => ScrollTrigger.refresh());
  }

  const MQ = {
    desktop: "(min-width: 1024px) and (prefers-reduced-motion: no-preference)",
    mobile: "(max-width: 1023px) and (prefers-reduced-motion: no-preference)",
    motion: "(prefers-reduced-motion: no-preference)",
    reduce: "(prefers-reduced-motion: reduce)",
  };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduced = () => window.matchMedia(MQ.reduce).matches;
  const pad = (n) => String(n).padStart(2, "0");
  const WORLDS = ["static", "slow", "bloom", "club"];

  /** Page-wide colour environment (CSS variables keyed on html[data-world]). */
  function setWorld(id) {
    if (WORLDS.includes(id)) document.documentElement.dataset.world = id;
  }

  /** Sections marked data-world-scope take over the palette while they cross the viewport middle. */
  function initWorldScroller() {
    const fixed = $("[data-set-world]");
    if (fixed) setWorld(fixed.dataset.setWorld);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setWorld(e.target.dataset.worldScope)),
      { rootMargin: "-50% 0px -50% 0px" }
    );
    $$("[data-world-scope]").forEach((el) => io.observe(el));
    return io;
  }

  /** data-inview="true|false" on every section so CSS ambient loops pause off screen. */
  const inviewIO = new IntersectionObserver(
    (entries) => entries.forEach((e) => (e.target.dataset.inview = e.isIntersecting ? "true" : "false")),
    { rootMargin: "100px" }
  );
  function watchInView(el) {
    el.dataset.inview = "false";
    inviewIO.observe(el);
  }

  const money = (cents) => {
    const format = cfg.moneyFormat || "${{amount}}";
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split(".");
    const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, int.replace(/\B(?=(\d{3})+(?!\d))/g, "."))
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
  };

  /* ------------------------------------------------------------------ 2 */

  const intro = {
    done: false,
    listeners: [],
    finish() {
      if (this.done) return;
      this.done = true;
      try {
        sessionStorage.setItem("slk-intro-seen", "1");
      } catch (e) {
        /* private mode — the intro simply shows again */
      }
      this.listeners.splice(0).forEach((fn) => fn());
    },
    then(fn) {
      if (this.done) fn();
      else this.listeners.push(fn);
    },
  };
  const introSkipped = document.documentElement.classList.contains("intro-seen");

  function initIntro() {
    const loader = $("[data-intro]");
    if (!loader || introSkipped) {
      loader?.remove();
      intro.finish();
      return;
    }
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    window.setTimeout(
      () => {
        document.body.style.overflow = overflow;
        loader.setAttribute("data-done", "");
        intro.finish();
        window.setTimeout(() => loader.remove(), 800);
      },
      reduced() ? 400 : 1000
    );
  }

  /* ------------------------------------------------------------------ 3 */

  const dialogs = {
    current: null,
    lastFocus: null,
    overflow: "",
    open(name) {
      const root = $(`[data-dialog="${name}"]`);
      if (!root) return false;
      if (this.current && this.current !== root) this.close(false);
      if (!this.current) {
        this.lastFocus = document.activeElement;
        this.overflow = document.body.style.overflow;
      }
      this.current = root;
      root.setAttribute("data-open", "");
      document.body.style.overflow = "hidden";
      $$(`[data-dialog-open="${name}"]`).forEach((b) => b.setAttribute("aria-expanded", "true"));
      $("[data-header]")?.removeAttribute("data-tucked");
      requestAnimationFrame(() => {
        const panel = $("[data-dialog-panel]", root);
        const target = $("[data-autofocus]", root) || panel;
        target?.focus({ preventScroll: true });
      });
      return true;
    },
    close(restore = true) {
      const root = this.current;
      if (!root) return;
      root.removeAttribute("data-open");
      $$(`[data-dialog-open="${root.dataset.dialog}"]`).forEach((b) => b.setAttribute("aria-expanded", "false"));
      this.current = null;
      document.body.style.overflow = this.overflow;
      if (restore) this.lastFocus?.focus?.({ preventScroll: true });
    },
  };

  function initDialogs() {
    document.addEventListener("click", (e) => {
      const opener = e.target.closest("[data-dialog-open]");
      if (opener) {
        // Cart link goes to /cart without JS; with JS the drawer opens instead.
        if (dialogs.open(opener.dataset.dialogOpen)) e.preventDefault();
        return;
      }
      if (e.target.closest("[data-dialog-close]")) {
        e.preventDefault();
        dialogs.close();
        return;
      }
      // Same-page anchors inside the mobile menu close it first.
      const menuLink = e.target.closest("[data-menu-link]");
      if (menuLink) dialogs.close(false);
    });

    document.addEventListener("keydown", (e) => {
      const root = dialogs.current;
      if (!root) return;
      if (e.key === "Escape") {
        e.preventDefault();
        dialogs.close();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = $("[data-dialog-panel]", root);
      const focusables = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])', panel).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  function initHeader() {
    const header = $("[data-header]");
    if (!header) return;
    const TOP = ["bg-gradient-to-b", "from-ink/50", "to-transparent", "py-5", "lg:py-7"];
    const SCROLLED = ["bg-ink/85", "py-3"];
    let last = window.scrollY;
    let scrolled = null;
    const onScroll = () => {
      const y = window.scrollY;
      const isScrolled = y > 40 || header.hasAttribute("data-solid");
      if (isScrolled !== scrolled) {
        scrolled = isScrolled;
        header.classList.remove(...(isScrolled ? TOP : SCROLLED));
        header.classList.add(...(isScrolled ? SCROLLED : TOP));
      }
      if (Math.abs(y - last) > 6) {
        const tuck = y > last && y > window.innerHeight * 0.9 && !dialogs.current;
        header.toggleAttribute("data-tucked", tuck);
        last = y;
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    header.addEventListener("focusin", () => header.removeAttribute("data-tucked"));

    // Active state for in-page nav links (#new-drop, #worlds, #custom) on the homepage.
    const links = $$("[data-nav-link]", header).filter((a) => a.hash && a.pathname.replace(/\/$/, "") === location.pathname.replace(/\/$/, ""));
    const targets = links.map((a) => document.getElementById(a.hash.slice(1))).filter(Boolean);
    if (!targets.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const link = links.find((a) => a.hash === `#${e.target.id}`);
          if (!link) return;
          if (e.isIntersecting) {
            links.forEach((a) => a.removeAttribute("aria-current"));
            link.setAttribute("aria-current", "true");
          } else if (link.getAttribute("aria-current") === "true") {
            link.removeAttribute("aria-current");
          }
        });
      },
      { rootMargin: "-45% 0px -45% 0px" }
    );
    targets.forEach((t) => io.observe(t));
  }

  function initSearch() {
    const root = $('[data-dialog="search"]');
    if (!root) return;
    const form = $("[data-predictive-search]", root);
    const input = $('input[name="q"]', form);
    const results = $("[data-search-results]", root);
    const suggestions = $("[data-search-suggestions]", root);
    let timer;
    let controller;

    const run = async () => {
      const q = input.value.trim();
      suggestions?.toggleAttribute("hidden", Boolean(q));
      if (!q) {
        results.innerHTML = "";
        return;
      }
      controller?.abort();
      controller = new AbortController();
      const url = `${cfg.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=12&resources[options][fields]=title,product_type,variants.title,tag,vendor&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const section = doc.querySelector(".shopify-section") || doc.body;
        results.innerHTML = section.innerHTML;
      } catch (err) {
        if (err.name !== "AbortError") results.innerHTML = "";
      }
    };

    input.addEventListener("input", () => {
      clearTimeout(timer);
      timer = setTimeout(run, 220);
    });
    $$("[data-search-suggestion]", root).forEach((b) =>
      b.addEventListener("click", () => {
        input.value = b.dataset.searchSuggestion;
        input.focus();
        run();
      })
    );
  }

  /* ------------------------------------------------------------------ 4 */

  const cart = {
    section: "cart-drawer",
    async request(url, options) {
      const res = await fetch(url, {
        ...options,
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest", ...(options.headers || {}) },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || cfg.strings.error || "Error");
      return data;
    },
    render(sections) {
      const html = sections?.[this.section];
      if (!html) return this.refresh();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector("[data-cart-body]");
      const body = $("[data-cart-body]");
      if (fresh && body) body.replaceWith(fresh);
      this.updateCount();
    },
    async refresh() {
      const res = await fetch(`${window.location.pathname}?sections=${this.section}`);
      const data = await res.json();
      this.render(data);
    },
    updateCount() {
      const body = $("[data-cart-body]");
      const count = body?.dataset.itemCount;
      if (count === undefined) return;
      $$("[data-cart-count]").forEach((el) => {
        if (el.textContent !== count) {
          el.textContent = count;
          el.classList.remove("count-bump");
          void el.offsetWidth;
          el.classList.add("count-bump");
        }
      });
      $$('[data-dialog-open="cart"][aria-label]').forEach((el) => el.setAttribute("aria-label", el.getAttribute("aria-label").replace(/\d+/, count)));
    },
    async add(formData) {
      formData.append("sections", this.section);
      formData.append("sections_url", window.location.pathname);
      const data = await this.request(`${cfg.routes.cartAdd}.js`, { method: "POST", body: formData });
      this.render(data.sections);
      dialogs.open("cart");
      this.status(cfg.strings.added);
    },
    async change(key, quantity) {
      const data = await this.request(`${cfg.routes.cartChange}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity, sections: this.section, sections_url: window.location.pathname }),
      });
      this.render(data.sections);
    },
    async swap(key, variantId, quantity) {
      await this.request(`${cfg.routes.cartChange}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      const data = await this.request(`${cfg.routes.cartAdd}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: this.section, sections_url: window.location.pathname }),
      });
      this.render(data.sections);
    },
    status(message) {
      const el = $("[data-cart-status]");
      if (el) el.textContent = message || "";
    },
  };

  function initCart() {
    // Quick add + product forms → Ajax. Without JS they post normally to /cart/add.
    document.addEventListener("submit", async (e) => {
      const form = e.target.closest("form[data-quick-add], form[data-product-form]");
      if (!form) return;
      e.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const message = form.querySelector("[data-form-message]");
      form.setAttribute("data-busy", "");
      if (message) message.textContent = "";
      try {
        await cart.add(new FormData(form));
      } catch (err) {
        if (message) message.textContent = err.message;
        else window.alert(err.message);
      } finally {
        form.removeAttribute("data-busy");
        button?.blur();
      }
    });

    // Drawer controls (the body is re-rendered, so delegate).
    document.addEventListener("click", async (e) => {
      const qty = e.target.closest("[data-cart-qty]");
      if (!qty) return;
      const line = qty.closest("[data-cart-line]");
      line?.setAttribute("data-busy", "");
      try {
        await cart.change(qty.dataset.cartQty, Number(qty.dataset.value));
      } catch (err) {
        cart.status(err.message);
        line?.removeAttribute("data-busy");
      }
    });
    document.addEventListener("change", async (e) => {
      const select = e.target.closest("[data-cart-size]");
      if (!select) return;
      const line = select.closest("[data-cart-line]");
      line?.setAttribute("data-busy", "");
      try {
        await cart.swap(select.dataset.cartSize, select.value, Number(select.dataset.quantity) || 1);
      } catch (err) {
        cart.status(err.message);
        await cart.refresh();
      }
    });
  }

  function initProductForm(section) {
    const form = $("[data-product-form]", section);
    const json = $("[data-product-json]", section);
    if (!form || !json) return;
    const variants = JSON.parse(json.textContent);
    const idInput = $("[data-variant-id]", form);
    const addButton = $("[data-add-button]", form);
    const addLabel = addButton?.querySelector("span:not([aria-hidden])");
    const addText = addLabel?.textContent;
    const priceEl = $("[data-price-current]", section);
    const compareEl = $("[data-price-compare]", section);
    const fieldsets = $$("[data-option-index]", form);

    const selected = () => fieldsets.map((fs) => $("[data-option-input]:checked", fs)?.value);
    const wornMap = (() => {
      try { return JSON.parse($("[data-worn-map]", section)?.textContent || "{}"); } catch { return {}; }
    })();

    const update = () => {
      const options = selected();
      const variant = variants.find((v) => v.options.every((o, i) => o === options[i]));
      fieldsets.forEach((fs, i) => {
        const valueLabel = $("[data-option-value]", fs);
        if (valueLabel) valueLabel.textContent = options[i] || "";
        // Mark values that don't combine with the other current choices.
        $$("[data-option-label]", fs).forEach((label) => {
          const candidate = options.slice();
          candidate[i] = label.dataset.optionLabel;
          const match = variants.find((v) => v.options.every((o, j) => o === candidate[j]));
          label.toggleAttribute("data-unavailable", !match || !match.available);
        });
      });
      if (!variant) {
        if (addButton) addButton.disabled = true;
        return;
      }
      idInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) {
        compareEl.textContent = variant.compare_at_price ? money(variant.compare_at_price) : "";
        compareEl.classList.toggle("hidden", !(variant.compare_at_price > variant.price));
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        if (addLabel) addLabel.textContent = variant.available ? addText : cfg.strings.soldOut;
      }
      // Show the chosen colour: swap the lead photo to the variant's own image.
      const lead = section.querySelector("[data-product-media] [data-media-id] img");
      const vsrc = variant.featured_image?.src;
      if (lead && vsrc && !initializing) {
        const sized = (w) => `${vsrc}${vsrc.includes("?") ? "&" : "?"}width=${w}`;
        const srcset = [540, 900, 1400, 1800].map((w) => `${sized(w)} ${w}w`).join(", ");
        [lead, section.querySelector("[data-closeup] img")].forEach((img) => {
          if (!img) return;
          img.srcset = srcset;
          img.src = sized(1400);
        });
        // On-model slide follows the chosen colour (map of colour → image from Liquid).
        const wornImg = section.querySelector("[data-worn] img");
        const wornSrc = wornImg && wornMap[options.find((o) => wornMap[o])];
        if (wornSrc) {
          const sized = (w) => wornSrc.replace(/width=\d+/, `width=${w}`);
          wornImg.srcset = [540, 900, 1400].map((w) => `${sized(w)} ${w}w`).join(", ");
          wornImg.src = sized(900);
        }
        // Mobile gallery is a swipe strip: bring the lead photo back into the strip.
        const strip = section.querySelector("[data-product-media]");
        if (strip && strip.scrollLeft > 0) strip.scrollTo({ left: 0, behavior: reduced() ? "auto" : "smooth" });
      }
      if (!initializing) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }
    };

    // First visit (no ?variant): preselect the colour that matches the main photo,
    // so the price, cart line and picture all agree.
    let initializing = true;
    const leadImg = section.querySelector("[data-product-media] [data-media-id] img");
    const file = (s) => (s || "").split("?")[0].split("/").pop();
    if (!new URL(window.location.href).searchParams.get("variant") && leadImg) {
      const leadFile = file(leadImg.currentSrc || leadImg.src);
      const stem = (s) => file(s).replace(/\.[a-z]+$/i, "").replace(/_\d+x\d*$/, "");
      const match = variants.find((v) => v.available && v.featured_image && stem(leadFile) === stem(v.featured_image.src));
      if (match) {
        fieldsets.forEach((fs, i) => {
          const input = $$("[data-option-input]", fs).find((el) => el.value === match.options[i]);
          if (input) input.checked = true;
        });
      }
    }

    form.addEventListener("change", (e) => {
      if (e.target.matches("[data-option-input]")) update();
    });
    const qtyInput = $("[data-qty-input]", form);
    $$("[data-qty-step]", form).forEach((b) =>
      b.addEventListener("click", () => {
        qtyInput.value = Math.max(1, (Number(qtyInput.value) || 1) + Number(b.dataset.qtyStep));
      })
    );
    update();
    initializing = false;
  }

  /* ------------------------------------------------------------------ 5 */

  function initCursor() {
    const el = $("[data-cursor-root]");
    if (!el || !hasGsap) return;
    const label = $(".cursor-label", el);
    const mql = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 1024px) and (prefers-reduced-motion: no-preference)");
    let cleanup = null;
    const enable = () => {
      document.documentElement.classList.add("has-custom-cursor");
      const xTo = gsap.quickTo(el, "x", { duration: 0.35, ease: "power3.out" });
      const yTo = gsap.quickTo(el, "y", { duration: 0.35, ease: "power3.out" });
      let visible = false;
      const move = (e) => {
        if (e.pointerType !== "mouse") return;
        xTo(e.clientX);
        yTo(e.clientY);
        if (!visible) {
          visible = true;
          gsap.set(el, { x: e.clientX, y: e.clientY });
          el.style.opacity = "1";
        }
      };
      const over = (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        if (t.closest("input, textarea, select, [contenteditable='true'], iframe")) return (el.dataset.state = "hidden");
        const labelled = t.closest("[data-cursor]");
        if (labelled?.dataset.cursor) {
          label.textContent = labelled.dataset.cursor;
          return (el.dataset.state = "label");
        }
        el.dataset.state = t.closest("a, button, [role='button'], summary, label[for]") ? "hover" : "default";
      };
      const leave = () => {
        visible = false;
        el.style.opacity = "0";
      };
      window.addEventListener("pointermove", move, { passive: true });
      document.addEventListener("pointerover", over, { passive: true });
      document.documentElement.addEventListener("pointerleave", leave);
      return () => {
        document.documentElement.classList.remove("has-custom-cursor");
        window.removeEventListener("pointermove", move);
        document.removeEventListener("pointerover", over);
        document.documentElement.removeEventListener("pointerleave", leave);
        el.style.opacity = "0";
      };
    };
    const sync = () => {
      cleanup?.();
      cleanup = mql.matches ? enable() : null;
    };
    sync();
    mql.addEventListener("change", sync);
  }

  function initMagnetic(root) {
    if (!hasGsap) return;
    const mm = gsap.matchMedia();
    mm.add("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)", () => {
      const offs = $$("[data-magnetic]", root).map((el) => {
        const xTo = gsap.quickTo(el, "x", { duration: 0.6, ease: "power3.out" });
        const yTo = gsap.quickTo(el, "y", { duration: 0.6, ease: "power3.out" });
        const move = (e) => {
          const r = el.getBoundingClientRect();
          xTo((e.clientX - (r.left + r.width / 2)) * 0.28);
          yTo((e.clientY - (r.top + r.height / 2)) * 0.28);
        };
        const leave = () => {
          xTo(0);
          yTo(0);
        };
        el.addEventListener("pointermove", move);
        el.addEventListener("pointerleave", leave);
        return () => {
          el.removeEventListener("pointermove", move);
          el.removeEventListener("pointerleave", leave);
        };
      });
      return () => offs.forEach((off) => off());
    });
    return () => mm.revert();
  }

  /** Pointer depth: moves `target` against the pointer inside `area`. */
  function pointerDepth(area, target, x, y, duration = 1) {
    const xTo = gsap.quickTo(target, "x", { duration, ease: "power3.out" });
    const yTo = gsap.quickTo(target, "y", { duration, ease: "power3.out" });
    const move = (e) => {
      xTo((e.clientX / window.innerWidth - 0.5) * x);
      yTo((e.clientY / window.innerHeight - 0.5) * y);
    };
    area.addEventListener("pointermove", move);
    return () => area.removeEventListener("pointermove", move);
  }

  /* ------------------------------------------------------------------ 6 */

  /** Each world moves differently (shared by Switch The Look + World Selector). */
  const WORLD_MOTION = {
    static: {
      ease: "power3.inOut",
      duration: 0.5,
      sceneFrom: { clipPath: "inset(0% 0% 0% 100%)", xPercent: 6 },
      sceneTo: { opacity: 0, xPercent: -4 },
      wordFrom: { xPercent: -18, opacity: 0 },
      reveal: { from: { clipPath: "inset(0% 100% 0% 0%)" }, duration: 0.8, ease: "expo.inOut" },
    },
    slow: {
      ease: "power2.out",
      duration: 0.8,
      sceneFrom: { opacity: 0, scale: 1.14 },
      sceneTo: { opacity: 0, scale: 0.98 },
      wordFrom: { yPercent: 22, opacity: 0, scale: 0.94 },
      reveal: { from: { opacity: 0, scale: 1.12 }, duration: 1.4, ease: "sine.out" },
    },
    bloom: {
      ease: "power4.out",
      duration: 0.72,
      sceneFrom: { clipPath: "inset(100% 0% 0% 0%)", yPercent: 8 },
      sceneTo: { opacity: 0, yPercent: -3 },
      wordFrom: { yPercent: 40, opacity: 0 },
      reveal: { from: { clipPath: "inset(100% 0% 0% 0%)", y: 40 }, duration: 1.2, ease: "power3.out" },
    },
    club: {
      ease: "expo.inOut",
      duration: 0.5,
      sceneFrom: { clipPath: "polygon(0% 0%, 0% 0%, -30% 100%, -30% 100%)" },
      sceneTo: { opacity: 0 },
      wordFrom: { xPercent: 24, skewX: -22, opacity: 0 },
      reveal: { from: { clipPath: "polygon(0% 0%, 0% 0%, -40% 100%, -40% 100%)" }, duration: 0.65, ease: "power4.inOut" },
    },
  };
  const SCENE_IN = { clipPath: "inset(0% 0% 0% 0%)", xPercent: 0, yPercent: 0, opacity: 1, scale: 1 };
  const CLUB_SCENE_IN = { clipPath: "polygon(0% 0%, 130% 0%, 100% 100%, -30% 100%)", opacity: 1 };

  /** Editorial masked line reveal for headings with [data-reveal]. */
  function initReveals(root) {
    if (!hasGsap) return;
    const mm = gsap.matchMedia();
    mm.add(MQ.motion, () => {
      $$("[data-reveal]", root).forEach((heading) => {
        gsap.from($$("[data-line]", heading), {
          yPercent: 112,
          rotate: 2,
          duration: 0.8,
          ease: "expo.out",
          stagger: 0.07,
          clearProps: "transform",
          scrollTrigger: { trigger: heading, start: "top 96%", once: true },
        });
      });
    });
    return () => mm.revert();
  }

  const SECTIONS = {
    hero(section) {
      if (!hasGsap) return;
      const q = gsap.utils.selector(section);
      const mm = gsap.matchMedia();
      mm.add(MQ.motion, () => {
        if (introSkipped || intro.done) return;
        gsap.set(q("[data-hero-letter]"), { yPercent: 110 });
        gsap.set(q("[data-hero-look]"), { yPercent: 100 });
        gsap.set(q("[data-hero-model]"), { autoAlpha: 0, yPercent: 8 });
        gsap.set(q("[data-hero-env]"), { scale: 1.12 });
        gsap.set(q("[data-hero-ui]"), { autoAlpha: 0, y: 20 });
        const tl = gsap.timeline({ paused: true, defaults: { ease: "expo.out" } });
        tl.to(q("[data-hero-env]"), { scale: 1, duration: 1.8 }, 0)
          .to(q("[data-hero-letter]"), { yPercent: 0, duration: 1, stagger: 0.035 }, 0.05)
          .to(q("[data-hero-model]"), { autoAlpha: 1, yPercent: 0, duration: 1.1 }, 0.2)
          .to(q("[data-hero-look]"), { yPercent: 0, duration: 0.9 }, 0.4)
          .to(q("[data-hero-ui]"), { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.05 }, 0.6);
        intro.then(() => tl.play());
        return () => tl.kill();
      });
      mm.add(`${MQ.desktop} and (hover: hover)`, () => {
        const layers = [
          { el: q("[data-depth='env']"), d: -14 },
          { el: q("[data-depth='switch']"), d: -8 },
          { el: q("[data-depth='model']"), d: 10 },
          { el: q("[data-depth='look']"), d: 14 },
        ].map(({ el, d }) => ({ d, x: gsap.quickTo(el, "x", { duration: 1, ease: "power3.out" }), y: gsap.quickTo(el, "y", { duration: 1, ease: "power3.out" }) }));
        let raf = 0;
        const move = (e) => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const nx = e.clientX / window.innerWidth - 0.5;
            const ny = e.clientY / window.innerHeight - 0.5;
            layers.forEach((l) => {
              l.x(nx * l.d);
              l.y(ny * l.d * 0.5);
            });
          });
        };
        section.addEventListener("pointermove", move);
        return () => {
          cancelAnimationFrame(raf);
          section.removeEventListener("pointermove", move);
        };
      });
      mm.add(MQ.desktop, () => {
        const tl = gsap.timeline({ defaults: { ease: "none" }, scrollTrigger: { trigger: section, start: "top top", end: "bottom bottom", scrub: 0.6 } });
        tl.to(q("[data-split='switch']"), { xPercent: -34 }, 0)
          .to(q("[data-split='look']"), { xPercent: 38 }, 0)
          .to(q("[data-split='model']"), { scale: 0.9, yPercent: 3 }, 0)
          .to(q("[data-split='env']"), { scale: 1.08 }, 0)
          .to(q("[data-split='ui']"), { autoAlpha: 0, y: -24, duration: 0.3 }, 0)
          .to(q("[data-split='shade']"), { autoAlpha: 0.8 }, 0.15)
          .fromTo(q("[data-split='lines'] span"), { scaleY: 0.35 }, { scaleY: 1, stagger: 0.05 }, 0.2);
      });
      mm.add(MQ.mobile, () => {
        gsap.to(q("[data-split='model']"), { yPercent: 8, ease: "none", scrollTrigger: { trigger: section, start: "top top", end: "bottom top", scrub: true } });
      });
      const offMagnetic = initMagnetic(section);
      return () => {
        mm.revert();
        offMagnetic?.();
      };
    },

    "world-selector"(section) {
      if (!hasGsap) return;
      const mm = gsap.matchMedia();
      mm.add(MQ.motion, () => {
        gsap.from($$("[data-grid-line]", section), { scaleY: 0, ease: "none", stagger: 0.08, scrollTrigger: { trigger: section, start: "top bottom", end: "top 20%", scrub: true } });
        $$("[data-world-panel]", section).forEach((panel) => {
          const m = WORLD_MOTION[panel.dataset.worldPanel] || WORLD_MOTION.static;
          const tl = gsap.timeline({ scrollTrigger: { trigger: panel, start: "top 85%", once: true } });
          tl.from($("[data-panel-frame]", panel), { ...m.reveal.from, duration: m.reveal.duration, ease: m.reveal.ease })
            .from($("[data-panel-media]", panel), { scale: 1.25, duration: m.reveal.duration + 0.4, ease: "expo.out" }, 0)
            .from($$("[data-panel-text]", panel), { yPercent: 105, duration: 0.7, stagger: 0.05, ease: "expo.out" }, m.reveal.duration * 0.5);
        });
      });
      mm.add(`${MQ.desktop} and (hover: hover)`, () => {
        const offs = $$("[data-world-panel]", section).map((panel) => {
          const media = $("[data-panel-depth]", panel);
          if (!media) return () => {};
          const xTo = gsap.quickTo(media, "x", { duration: 0.9, ease: "power3.out" });
          const yTo = gsap.quickTo(media, "y", { duration: 0.9, ease: "power3.out" });
          const move = (e) => {
            const r = panel.getBoundingClientRect();
            xTo(((e.clientX - r.left) / r.width - 0.5) * -24);
            yTo(((e.clientY - r.top) / r.height - 0.5) * -16);
          };
          const leave = () => {
            xTo(0);
            yTo(0);
          };
          panel.addEventListener("pointermove", move);
          panel.addEventListener("pointerleave", leave);
          return () => {
            panel.removeEventListener("pointermove", move);
            panel.removeEventListener("pointerleave", leave);
          };
        });
        return () => offs.forEach((off) => off());
      });
      return () => mm.revert();
    },

    "switch-the-look"(section) {
      const worlds = JSON.parse($("[data-switch-worlds]", section)?.textContent || "[]");
      const scenes = $$("[data-switch-scene]", section);
      const words = $$("[data-switch-word]", section);
      const moods = $$("[data-switch-mood]", section);
      const cards = $$("[data-switch-card]", section);
      const bars = $$("[data-switch-progress]", section);
      const tabs = $$("[data-switch-tab]", section);
      const indexEl = $("[data-switch-index]", section);
      const stage = $("[data-switch-stage]", section);
      const AUTOPLAY = Number(section.dataset.autoplay) || 0;
      const count = scenes.length;
      if (!count) return;

      let index = 0;
      let autoplay = AUTOPLAY > 0;
      let inView = false;
      let paused = false;
      let timer = null;
      let barTween = null;

      const running = () => autoplay && inView && !paused && !reduced() && hasGsap;

      const paint = (i) => {
        const w = worlds[i] || {};
        section.style.background = w.bg || "";
        section.style.color = w.text || "";
        section.style.setProperty("--switch-accent", w.world === "bloom" ? "#A65A3A" : w.accent || "currentColor");
        section.toggleAttribute("data-light", w.world === "bloom");
        if (inView) setWorld(w.world);
      };

      const progress = () => {
        barTween?.kill();
        bars.forEach((bar, j) => {
          if (hasGsap) gsap.set(bar, { scaleX: j < index ? 1 : j === index && !running() ? 1 : 0 });
          else bar.style.transform = `scaleX(${j <= index ? 1 : 0})`;
        });
        if (running()) barTween = gsap.fromTo(bars[index], { scaleX: 0 }, { scaleX: 1, duration: AUTOPLAY / 1000, ease: "none" });
      };

      const schedule = () => {
        timer?.kill?.();
        clearTimeout(timer);
        timer = null;
        if (running()) timer = gsap.delayedCall(AUTOPLAY / 1000, () => go(index + 1, false));
        progress();
      };

      const veil = (world) => {
        if (!hasGsap || reduced()) return;
        const el = $(`[data-veil="${world}"]`, section);
        if (!el) return;
        const vw = window.innerWidth;
        if (world === "static") gsap.fromTo(el, { x: -0.3 * vw }, { x: 1.3 * vw, duration: 0.5, ease: "power3.inOut" });
        if (world === "slow") gsap.fromTo(el, { opacity: 0, yPercent: 12, scale: 1.1 }, { keyframes: { opacity: [0, 0.95, 0] }, yPercent: -10, scale: 1, duration: 0.8, ease: "power2.out" });
        if (world === "bloom") gsap.fromTo(el, { opacity: 0.95, yPercent: 0 }, { opacity: 0, yPercent: -30, duration: 0.75, ease: "power4.out" });
        if (world === "club") gsap.fromTo(el, { x: -0.4 * vw }, { x: 1.4 * vw, duration: 0.6, ease: "expo.inOut" });
      };

      const go = (next, user = true) => {
        const target = (next + count) % count;
        if (user) autoplay = false;
        if (target === index) return schedule();
        const prev = index;
        index = target;
        const w = worlds[index]?.world || "static";
        const m = WORLD_MOTION[w] || WORLD_MOTION.static;
        const outM = WORLD_MOTION[worlds[prev]?.world] || WORLD_MOTION.static;
        const motion = hasGsap && !reduced();

        // scene
        const inScene = scenes[index];
        const outScene = scenes[prev];
        scenes.forEach((s, j) => {
          if (j !== index && j !== prev) {
            s.removeAttribute("data-active");
            s.removeAttribute("data-leaving");
          }
        });
        inScene.setAttribute("data-active", "");
        inScene.style.zIndex = "2";
        outScene.removeAttribute("data-active");
        outScene.setAttribute("data-leaving", "");
        outScene.style.zIndex = "1";
        const settle = () => {
          outScene.removeAttribute("data-leaving");
          outScene.style.zIndex = "";
          if (hasGsap) gsap.set(outScene, { clearProps: "all" });
        };
        if (motion) {
          gsap.killTweensOf([inScene, outScene]);
          gsap.to(outScene, { ...outM.sceneTo, duration: outM.duration, ease: outM.ease, onComplete: settle });
          gsap.fromTo(inScene, { ...SCENE_IN, ...m.sceneFrom }, { ...(w === "club" ? CLUB_SCENE_IN : SCENE_IN), duration: m.duration, ease: m.ease, clearProps: w === "club" ? "clipPath" : "" });
        } else if (hasGsap) {
          gsap.fromTo(inScene, { opacity: 0 }, { opacity: 1, duration: 0.2, onComplete: settle });
        } else settle();

        // word
        words.forEach((el, j) => {
          if (j === index) {
            if (motion) gsap.fromTo(el, { ...m.wordFrom }, { xPercent: 0, yPercent: 0, scale: 1, skewX: w === "club" ? -10 : 0, opacity: 0.95, duration: Math.min(0.85, m.duration + 0.15), ease: m.ease, delay: 0.08 });
            else el.style.opacity = "0.95";
          } else if (motion) gsap.to(el, { opacity: 0, duration: 0.2 });
          else el.style.opacity = "0";
        });

        // mood line
        moods.forEach((el, j) => {
          if (motion) gsap.to(el, { opacity: j === index ? 1 : 0, y: j === index ? 0 : -6, duration: 0.35, delay: j === index ? 0.15 : 0 });
          else el.style.opacity = j === index ? "1" : "0";
        });

        // the piece
        cards.forEach((el, j) => {
          const on = j === index;
          el.classList.toggle("invisible", !on);
          if (!on) {
            el.style.opacity = "0";
            return;
          }
          if (motion) gsap.fromTo(el, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.3, delay: 0.2, ease: "expo.out" });
          else el.style.opacity = "1";
        });

        // index + tabs
        if (indexEl) {
          indexEl.textContent = pad(index + 1);
          if (motion) gsap.fromTo(indexEl, { yPercent: 40, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.3, ease: m.ease });
        }
        tabs.forEach((t) => {
          const on = Number(t.dataset.switchTab) === index;
          t.setAttribute("aria-selected", on ? "true" : "false");
          t.setAttribute("tabindex", on ? "0" : "-1");
        });

        veil(w);
        paint(index);
        schedule();
      };

      // initial paint
      cards.forEach((el, j) => {
        el.classList.toggle("invisible", j !== 0);
        el.style.opacity = j === 0 ? "1" : "0";
      });
      paint(0);

      tabs.forEach((t) => t.addEventListener("click", () => go(Number(t.dataset.switchTab))));
      $$("[data-switch-tabs]", section).forEach((list) =>
        list.addEventListener("keydown", (e) => {
          const delta = ["ArrowRight", "ArrowDown"].includes(e.key) ? 1 : ["ArrowLeft", "ArrowUp"].includes(e.key) ? -1 : 0;
          if (!delta) return;
          e.preventDefault();
          go(index + delta);
          $$("[data-switch-tab]", list).find((t) => Number(t.dataset.switchTab) === index)?.focus();
        })
      );
      $("[data-switch-prev]", section)?.addEventListener("click", () => go(index - 1));
      $("[data-switch-next]", section)?.addEventListener("click", () => go(index + 1));

      // swipe
      let startX = null;
      stage.addEventListener("pointerdown", (e) => (startX = e.clientX));
      stage.addEventListener("pointerup", (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        startX = null;
        if (dx < -60) go(index + 1);
        else if (dx > 60) go(index - 1);
      });
      stage.addEventListener("pointercancel", () => (startX = null));

      // pause on hover / focus
      const pause = (v) => {
        paused = v;
        schedule();
      };
      section.addEventListener("mouseenter", () => pause(true));
      section.addEventListener("mouseleave", () => pause(false));
      section.addEventListener("focusin", () => pause(true));
      section.addEventListener("focusout", (e) => !section.contains(e.relatedTarget) && pause(false));

      const io = new IntersectionObserver(
        ([e]) => {
          inView = e.intersectionRatio > 0.55;
          if (inView) setWorld(worlds[index]?.world);
          schedule();
        },
        { threshold: [0, 0.55, 1] }
      );
      io.observe(section);

      section._switchGo = (i) => go(i);
      return () => {
        io.disconnect();
        timer?.kill?.();
        barTween?.kill();
      };
    },

    "brand-marquee"(section) {
      if (!hasGsap) return;
      const mm = gsap.matchMedia();
      mm.add(MQ.motion, () => {
        gsap.fromTo($("[data-beam]", section), { xPercent: -140, rotate: -18 }, { xPercent: 260, rotate: -8, ease: "none", scrollTrigger: { trigger: section, start: "top bottom", end: "bottom top", scrub: 0.4 } });
      });
      return () => mm.revert();
    },

    "new-drop"(section) {
      if (!hasGsap) return;
      const track = $("[data-drop-track]", section);
      const bar = $("[data-drop-progress]", section);
      const mm = gsap.matchMedia();
      mm.add(MQ.desktop, () => {
        const distance = () => track.scrollWidth - window.innerWidth;
        const tween = gsap.to(track, {
          x: () => -distance(),
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: () => `+=${distance()}`,
            pin: true,
            scrub: 0.8,
            invalidateOnRefresh: true,
            anticipatePin: 1,
            onUpdate: (self) => bar && (bar.style.transform = `scaleX(${self.progress})`),
          },
        });
        $$("[data-drop-media]", section).forEach((media) => {
          gsap.fromTo(media, { xPercent: -4 }, { xPercent: 4, ease: "none", scrollTrigger: { trigger: media, containerAnimation: tween, start: "left right", end: "right left", scrub: true } });
        });
        const refresh = () => ScrollTrigger.refresh();
        window.addEventListener("load", refresh);
        return () => window.removeEventListener("load", refresh);
      });
      return () => mm.revert();
    },

    "brand-film"(section) {
      const timecode = $("[data-timecode]", section);
      let frames = 0;
      let interval = null;
      const io = new IntersectionObserver(([e]) => {
        clearInterval(interval);
        if (!e.isIntersecting || reduced() || !timecode) return;
        interval = setInterval(() => {
          frames = (frames + 1) % (30 * 24);
          timecode.textContent = `00:00:${pad(Math.floor(frames / 24))}:${pad(frames % 24)}`;
        }, 1000 / 24);
      });
      io.observe(section);
      let mm;
      if (hasGsap) {
        mm = gsap.matchMedia();
        mm.add(MQ.motion, () => {
          gsap.fromTo($("[data-film-still]", section), { scale: 1 }, { scale: 1.06, ease: "none", scrollTrigger: { trigger: section, start: "top bottom", end: "bottom top", scrub: true } });
          gsap.from($$("[data-film-word]", section), { yPercent: 115, opacity: 0, ease: "none", stagger: 0.25, scrollTrigger: { trigger: section, start: "top 70%", end: "center 55%", scrub: 0.8 } });
          gsap.fromTo($("[data-letterbox]", section), { scaleY: 1.6 }, { scaleY: 1, ease: "none", scrollTrigger: { trigger: section, start: "top bottom", end: "top top", scrub: true } });
        });
      }
      return () => {
        io.disconnect();
        clearInterval(interval);
        mm?.revert();
      };
    },

    "editorial-story"(section) {
      if (!hasGsap) return;
      const mm = gsap.matchMedia();
      mm.add(MQ.motion, () => {
        gsap.fromTo($("[data-parallax]", section), { yPercent: -6 }, { yPercent: 6, ease: "none", scrollTrigger: { trigger: section, start: "top bottom", end: "bottom top", scrub: true } });
        gsap.from($$("[data-story-line]", section), { yPercent: 110, duration: 1.2, ease: "power3.out", stagger: 0.12, clearProps: "transform", scrollTrigger: { trigger: section, start: "top 60%", once: true } });
      });
      mm.add(`${MQ.desktop} and (hover: hover)`, () => pointerDepth(section, $("[data-story-depth]", section), -14, -10, 1.2));
      return () => mm.revert();
    },

    "custom-teaser"(section) {
      if (!hasGsap) return;
      const SCATTER = [
        { x: -220, y: -140, r: -18 },
        { x: 220, y: 160, r: 16 },
        { x: -200, y: 170, r: 22 },
        { x: 240, y: -170, r: -24 },
        { x: 0, y: -240, r: 8 },
      ];
      const mm = gsap.matchMedia();
      mm.add(MQ.motion, () => {
        // Time-based (not scrubbed): fragments land together, resolved ~0.75s after entry.
        const tl = gsap.timeline({ scrollTrigger: { trigger: $("[data-still]", section), start: "top 80%", once: true } });
        $$("[data-layer]", section).forEach((layer, i) => {
          const s = SCATTER[i % SCATTER.length];
          tl.from(layer, { x: s.x * 0.6, y: s.y * 0.6, rotate: s.r, scale: 1.15, autoAlpha: 0, ease: "expo.out", duration: 0.6 }, i * 0.035);
        });
        tl.from($$("[data-callout]", section), { autoAlpha: 0, x: (i) => (i < 2 ? -16 : 16), stagger: 0.04, duration: 0.35, ease: "power3.out" }, 0.25);
      });
      return () => mm.revert();
    },

    newsletter(section) {
      const success = $("[data-newsletter-success]", section);
      if (success) {
        section.scrollIntoView({ block: "center" });
        success.focus({ preventScroll: true });
      }
    },

    "main-product"(section) {
      initProductForm(section);
    },

    async "product-recommendations"(section) {
      const url = section.dataset.url;
      if (!url || section.dataset.loaded) return;
      section.dataset.loaded = "true";
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const fresh = doc.querySelector("[data-section-type='product-recommendations']");
        if (fresh && fresh.innerHTML.trim()) section.innerHTML = fresh.innerHTML;
        else section.remove();
      } catch (e) {
        /* recommendations are optional */
      }
    },

    "main-collection"(section) {
      $$("form[data-auto-submit] select", section).forEach((select) => select.addEventListener("change", () => select.form.submit()));
    },
  };

  /* ------------------------------------------------------------------ 7 */

  const cleanups = new Map();

  function mountSection(section) {
    const type = section.dataset.sectionType;
    watchInView(section);
    const offReveal = initReveals(section);
    let off;
    try {
      off = SECTIONS[type]?.(section);
    } catch (err) {
      console.error(`[sleekious] ${type}`, err);
    }
    cleanups.set(section, () => {
      offReveal?.();
      if (typeof off === "function") off();
    });
  }

  function unmountSection(section) {
    cleanups.get(section)?.();
    cleanups.delete(section);
  }

  function boot() {
    initIntro();
    initWorldScroller();
    initDialogs();
    initHeader();
    initSearch();
    initCart();
    if (cfg.cursor) initCursor();
    $$("[data-section-type]").forEach(mountSection);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // Theme editor: re-mount a section after it is re-rendered, clean up before it goes.
  document.addEventListener("shopify:section:load", (e) => {
    $$("[data-section-type]", e.target).forEach(mountSection);
    if (hasGsap) ScrollTrigger.refresh();
    initWorldScroller();
  });
  document.addEventListener("shopify:section:unload", (e) => {
    $$("[data-section-type]", e.target).forEach(unmountSection);
  });
  document.addEventListener("shopify:block:select", (e) => {
    const section = e.target.closest("[data-section-type='switch-the-look']");
    if (!section?._switchGo) return;
    const scene = e.target.closest("[data-switch-scene]");
    if (scene) section._switchGo(Number(scene.dataset.switchScene));
  });
})();
